/* bsb-cart.js

   Exposes:
     window.bsbCartRegister(root)

   Dispatches: document event `cart:change`
     detail: { cart, source: 'bsb-cart' }
   Dispatches: `bsb-cart:added` on a surface's .bsb-crt root after an add,
     detail: { rows } — the rows that are new or went up, already flashing
   Listens: `bsb-cart:shown` on a surface's .bsb-crt root — dispatch it when a hidden
     surface becomes visible, so the sticky checkout bar re-checks */
(() => {
  'use strict';

  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const ROW = '.bsb-crt__item, .bsb-crt__vrow';
  const surfaces = [];
  const pending = new Map();
  let appliedCodes = null;
  let notice = null;
  let chain = Promise.resolve();
  let inFlight = 0;
  const addonsInFlight = new Set();

  const itemsIn = (surface) => Array.from(surface.root.querySelectorAll(ROW));

  const itemByKey = (surface, key) => itemsIn(surface).find((el) => el.dataset.key === key) || null;

  const addonAdds = (surface) =>
    Array.from(surface.root.querySelectorAll('[data-addon-add]:not([disabled])'));

  const announce = (surface, message) => {
    const el = surface.statusEl;
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => {
      el.textContent = message;
    });
  };

  const setBusy = (on) => {
    inFlight = Math.max(0, inFlight + (on ? 1 : -1));
    surfaces.forEach((s) => {
      const inner = s.root.querySelector('.bsb-crt__inner');
      if (inner) inner.setAttribute('aria-busy', inFlight > 0 ? 'true' : 'false');
    });
  };

  const markPending = (key, on) => {
    surfaces.forEach((s) => {
      const item = itemByKey(s, key);
      if (item) item.classList.toggle('is-pending', on);
    });
  };

  const applyPending = (surface) => {
    pending.forEach((quantity, key) => {
      const item = itemByKey(surface, key);
      if (!item) return;
      item.classList.add('is-pending');
      const valueEl = item.querySelector('.bsb-qty__value');
      if (valueEl) valueEl.textContent = quantity;
    });
  };

  const visibleIn = (root, selector) =>
    Array.from(root.querySelectorAll(selector)).find((el) => el.getClientRects().length > 0) || null;

  const recordFocus = (surface) => {
    const el = document.activeElement;
    if (!el || !surface.root.contains(el)) return null;
    const item = el.closest(ROW);
    const key = item ? item.dataset.key : null;
    const index = item ? itemsIn(surface).indexOf(item) : -1;
    if (el.classList.contains('bsb-qty__btn')) return { key, index, role: 'qty', delta: el.dataset.qtyDelta };
    if (el.classList.contains('bsb-crt__item-remove')) return { key, index, role: 'remove' };
    if (el.hasAttribute('data-addon-add')) {
      const adds = addonAdds(surface);
      return { key: null, index: -1, role: 'addon', at: adds.indexOf(el) };
    }
    if (el.classList.contains('bsb-crt__group-remove')) {
      const group = el.closest('.bsb-crt__group');
      const firstRow = group ? group.querySelector(ROW) : null;
      return { key: null, index: firstRow ? itemsIn(surface).indexOf(firstRow) : -1, role: 'remove' };
    }
    return { key: null, index: -1, role: 'other', id: el.id || null };
  };

  const restoreFocus = (surface, desc) => {
    if (!desc) return;
    const item = desc.key ? itemByKey(surface, desc.key) : null;

    if (item) {
      let target = null;
      if (desc.role === 'qty') {
        target = item.querySelector(`.bsb-qty__btn[data-qty-delta="${desc.delta}"]`);
        if (!target || target.disabled) target = item.querySelector('.bsb-qty__btn:not([disabled])');
      } else if (desc.role === 'remove') {
        target = visibleIn(item, '.bsb-crt__item-remove');
      }
      if (target && !target.disabled) {
        target.focus();
        return;
      }
    }

    if (desc.role === 'addon') {
      const adds = addonAdds(surface);
      if (adds.length) {
        adds[Math.min(Math.max(desc.at, 0), adds.length - 1)].focus();
        return;
      }
      const grid = surface.root.querySelector('.bsb-crt__addon-grid');
      if (grid) {
        grid.setAttribute('tabindex', '-1');
        grid.focus();
        return;
      }
    }

    if (desc.role === 'other') {
      const same = desc.id ? document.getElementById(desc.id) : null;
      const target =
        (same && surface.root.contains(same) ? same : null) || surface.root.querySelector('[data-bsb-cd-close]');
      if (target) {
        target.focus();
        return;
      }
    }

    const items = desc.role === 'other' ? [] : itemsIn(surface);
    if (items.length) {
      const at = desc.index >= 0 ? Math.min(desc.index, items.length - 1) : 0;
      const fallback =
        visibleIn(items[at], '.bsb-crt__item-remove') || items[at].querySelector('a');
      if (fallback) {
        fallback.focus();
        return;
      }
    }

    const heading =
      surface.root.querySelector('.bsb-crt__empty-title') || surface.root.querySelector('.bsb-crt__heading');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    }
  };

  const snapshot = (surface) => {
    const quantities = new Map();
    itemsIn(surface).forEach((el) => quantities.set(el.dataset.key, currentQty(el, el.dataset.key)));
    return quantities;
  };

  const flashAdded = (surface, before) => {
    const rows = itemsIn(surface).filter((el) => {
      const was = before.get(el.dataset.key);
      return was === undefined || currentQty(el, el.dataset.key) > was;
    });
    if (!rows.length) return;
    rows.forEach((el) => el.classList.add('is-new'));
    surface.root.dispatchEvent(new CustomEvent('bsb-cart:added', { bubbles: true, detail: { rows } }));
  };

  const watchSticky = (surface) => {
    if (surface.stickyObserver) surface.stickyObserver.disconnect();
    surface.stickyObserver = null;
    const bar = surface.root.querySelector('[data-bsb-sticky]');
    const target = surface.root.querySelector('form .bsb-crt__checkout');
    if (!bar || !target || !('IntersectionObserver' in window)) return;
    const anyDirection = bar.dataset.bsbSticky === 'any';
    surface.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
        const rendered = entry.boundingClientRect.height > 0;
        const show =
          rendered && !entry.isIntersecting && (anyDirection || entry.boundingClientRect.top > rootTop);
        bar.classList.toggle('is-shown', show);
        if (show) bar.removeAttribute('inert');
        else bar.setAttribute('inert', '');
      },
      { root: target.closest('[data-bsb-keep-scroll]') }
    );
    surface.stickyObserver.observe(target);
  };

  const swap = (surface, html, before) => {
    const inner = surface.root.querySelector('.bsb-crt__inner');
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.bsb-crt__inner');
    if (!inner || !parsed) return false;
    const kept = Array.from(inner.querySelectorAll('[data-bsb-keep-scroll]'), (el) => el.scrollTop);
    const stickyShown = Boolean(inner.querySelector('[data-bsb-sticky].is-shown'));
    const payOpen = Boolean(inner.querySelector('[data-bsb-cd-pay].is-open'));
    const freightText = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');
    const freightBefore = freightText(inner.querySelector('.bsb-crt__freight-msg'));
    const hadPump = Boolean(inner.querySelector('.bsb-crt__pump'));
    inner.innerHTML = parsed.innerHTML;
    inner.querySelectorAll('[data-bsb-keep-scroll]').forEach((el, i) => {
      if (kept[i] !== undefined) el.scrollTop = kept[i];
    });
    const freightAfter = freightText(inner.querySelector('.bsb-crt__freight-msg'));
    surface.freightNote = freightAfter && freightAfter !== freightBefore ? freightAfter : '';
    const pay = payOpen ? inner.querySelector('[data-bsb-cd-pay]') : null;
    if (pay) {
      pay.classList.add('is-open');
      const toggle = pay.querySelector('[aria-expanded]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      const payBody = pay.querySelector('.bsb-cd__pay-body');
      if (payBody) payBody.removeAttribute('inert');
    }
    const bar = inner.querySelector('[data-bsb-sticky]');
    if (bar && stickyShown) {
      bar.classList.add('is-shown');
      bar.removeAttribute('inert');
    }
    watchSticky(surface);
    applyPending(surface);
    paintPromo(surface);
    if (before) {
      const pump = hadPump ? null : inner.querySelector('.bsb-crt__pump');
      if (pump) pump.classList.add('is-new');
      flashAdded(surface, before);
    }
    return true;
  };

  const resync = (surface, desc, flash) => {
    if (!surface.sectionId) return Promise.resolve(false);
    return fetch(`${window.location.pathname}?section_id=${encodeURIComponent(surface.sectionId)}`)
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error('section fetch failed'))))
      .then((html) => {
        const from = desc === undefined ? recordFocus(surface) : desc;
        const done = swap(surface, html, flash ? snapshot(surface) : null);
        if (done) restoreFocus(surface, from);
        return done;
      })
      .catch(() => false);
  };

  const resyncAll = (flash) =>
    Promise.all(surfaces.map((s) => resync(s, undefined, flash))).then((r) => r.some(Boolean));

  const updateCartCount = (cart) => {
    if (!cart || typeof cart.item_count !== 'number') return;
    const count = cart.item_count;

    document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], .icon-cart-ball__count')
      .forEach((el) => { el.textContent = count; });

    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      bubble.hidden = count === 0;
    });

    document.querySelectorAll('#cart-icon-bubble, .bsb-nm__cart-link, .mega-menu__cart-icon')
      .forEach((link) => {
        const label = link.getAttribute('aria-label');
        if (label) link.setAttribute('aria-label', label.replace(/\(\d+\)\s*$/, `(${count})`));
      });
  };

  const announceCart = (surface, cart, lead = '') => {
    const totalRoot = surface.root.querySelector('.bsb-crt__checkout-price');
    const totalEl = totalRoot ? totalRoot.querySelector('.bsb-price__current') || totalRoot : null;
    const total = totalEl ? totalEl.textContent.trim() : '';
    const items = cart.item_count === 1 ? '1 item' : `${cart.item_count} items`;
    const summary = total ? `Cart updated. ${items}, total ${total}.` : `Cart updated. ${items}.`;
    const freight = surface.freightNote ? ` ${surface.freightNote}` : '';
    surface.freightNote = '';
    announce(surface, `${lead ? `${lead} ` : ''}${summary}${freight}`);
  };

  const send = (origin, key, quantity) => {
    setBusy(true);
    markPending(key, true);
    const desc = recordFocus(origin);

    const ids = surfaces.map((s) => s.sectionId).filter(Boolean);

    const body = JSON.stringify({
      id: key,
      quantity,
      sections: ids,
      sections_url: window.location.pathname
    });

    return fetch(`${shopRoot}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          announce(origin, data && data.description ? data.description : 'That quantity is not available.');
          return resyncAll();
        }

        readCodes(data);

        let swapped = false;
        surfaces.forEach((s) => {
          const html = data.sections && s.sectionId ? data.sections[s.sectionId] : null;
          if (html && swap(s, html)) swapped = true;
        });

        return Promise.resolve(swapped ? true : resyncAll()).then((settled) => {
          if (!settled) {
            window.location.reload();
            return;
          }
          if (swapped) restoreFocus(origin, desc);
          updateCartCount(data);
          const line = Array.isArray(data.items) ? data.items.find((entry) => entry.key === key) : null;
          const lead = line ? `${line.product_title}, quantity ${line.quantity}.` : 'Item removed.';
          announceCart(origin, data, lead);
          document.dispatchEvent(
            new CustomEvent('cart:change', { detail: { cart: data, source: 'bsb-cart' } })
          );
        });
      })
      .catch(() => {
        announce(origin, 'Something went wrong updating your cart.');
        return resyncAll();
      })
      .finally(() => {
        if (!pending.has(key)) markPending(key, false);
        setBusy(false);
      });
  };

  const addItem = (origin, variantId, title) => {
    setBusy(true);
    const desc = recordFocus(origin);
    const before = new Map(surfaces.map((s) => [s, snapshot(s)]));

    const ids = surfaces.map((s) => s.sectionId).filter(Boolean);

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: ids,
      sections_url: window.location.pathname
    });

    return fetch(`${shopRoot}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          announce(origin, data && data.description ? data.description : 'That item could not be added.');
          return resyncAll();
        }

        let swapped = false;
        surfaces.forEach((s) => {
          const html = data.sections && s.sectionId ? data.sections[s.sectionId] : null;
          if (html && swap(s, html, before.get(s))) swapped = true;
        });

        return Promise.resolve(swapped ? true : resyncAll())
          .then((settled) => {
            if (!settled) {
              window.location.reload();
              return null;
            }
            if (swapped) restoreFocus(origin, desc);
            return fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } })
              .then((response) => (response.ok ? response.json() : null))
              .catch(() => null);
          })
          .then((cart) => {
            if (!cart) return;
            readCodes(cart);
            updateCartCount(cart);
            const freight = origin.freightNote ? ` ${origin.freightNote}` : '';
            origin.freightNote = '';
            announce(origin, `${title || 'Item'} added to your cart.${freight}`);
            document.dispatchEvent(
              new CustomEvent('cart:change', { detail: { cart, source: 'bsb-cart' } })
            );
          });
      })
      .catch(() => {
        announce(origin, 'Something went wrong adding that item.');
        return resyncAll();
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const sendBulk = (origin, keys) => {
    setBusy(true);
    keys.forEach((key) => markPending(key, true));
    const desc = recordFocus(origin);

    const updates = {};
    keys.forEach((key) => { updates[key] = 0; });

    const body = JSON.stringify({
      updates,
      sections: surfaces.map((s) => s.sectionId).filter(Boolean),
      sections_url: window.location.pathname
    });

    return fetch(`${shopRoot}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          announce(origin, 'Something went wrong updating your cart.');
          return resyncAll();
        }

        readCodes(data);

        let swapped = false;
        surfaces.forEach((s) => {
          const html = data.sections && s.sectionId ? data.sections[s.sectionId] : null;
          if (html && swap(s, html)) swapped = true;
        });

        return Promise.resolve(swapped ? true : resyncAll()).then((settled) => {
          if (!settled) {
            window.location.reload();
            return;
          }
          restoreFocus(origin, desc);
          updateCartCount(data);
          announceCart(origin, data);
          document.dispatchEvent(
            new CustomEvent('cart:change', { detail: { cart: data, source: 'bsb-cart' } })
          );
        });
      })
      .catch(() => {
        announce(origin, 'Something went wrong updating your cart.');
        return resyncAll();
      })
      .finally(() => {
        keys.forEach((key) => markPending(key, false));
        setBusy(false);
      });
  };

  const activeCodes = () => (appliedCodes || []).slice();

  const hasCode = (code) =>
    activeCodes().some((applied) => applied.toLowerCase() === code.toLowerCase());

  const readCodes = (cart) => {
    if (!cart || !Array.isArray(cart.discount_codes)) return null;

    appliedCodes = cart.discount_codes
      .filter((entry) => entry && entry.applicable)
      .map((entry) => entry.code);

    return cart.discount_codes;
  };

  const paintNotice = (surface) => {
    const box = surface.root.querySelector('[data-bsb-promo-notice]');
    if (!box) return;

    const text = box.querySelector('[data-bsb-promo-msg]');
    if (text) text.textContent = '';

    if (!notice) {
      box.hidden = true;
      return;
    }

    if (text) {
      const code = document.createElement('strong');
      code.textContent = notice.code;
      text.appendChild(code);
      text.appendChild(document.createTextNode(` ${notice.text}`));
    }

    box.hidden = false;
  };

  const paintPromo = (surface) => {
    paintNotice(surface);

    const list = surface.root.querySelector('[data-bsb-promo-applied]');
    const template = surface.root.querySelector('[data-bsb-promo-template]');
    if (!list || !template) return;

    const codes = activeCodes();
    list.textContent = '';

    codes.forEach((code) => {
      const node = template.content.firstElementChild.cloneNode(true);
      const remove = node.querySelector('.bsb-crt__promo-remove');

      node.querySelector('.bsb-crt__promo-code-name').textContent = code;
      remove.dataset.promoRemove = code;
      remove.querySelector('.bsb-visually-hidden').textContent = `Remove discount code ${code}`;

      list.appendChild(node);
    });

    list.hidden = codes.length === 0;
  };

  const paintAll = () => surfaces.forEach(paintPromo);

  const setNotice = (code, text) => {
    notice = code ? { code, text } : null;
    surfaces.forEach(paintNotice);
  };

  const focusPromo = (surface, restore) => {
    const input = surface.root.querySelector('[data-bsb-promo-input]');
    if (!input) return;
    if (restore) {
      input.value = restore;
      input.focus();
      input.select();
      return;
    }
    input.focus();
  };

  const sendDiscount = (origin, codes, added) => {
    setBusy(true);
    const apply = Boolean(added);
    const before = activeCodes();
    const applyBtn = origin.root.querySelector('[data-bsb-promo-apply]');
    if (applyBtn) applyBtn.disabled = true;
    setNotice(null);

    const body = JSON.stringify({
      discount: codes.join(','),
      sections: surfaces.map((s) => s.sectionId).filter(Boolean),
      sections_url: window.location.pathname
    });

    return fetch(`${shopRoot}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          announce(origin, 'We could not update your discount code.');
          return resyncAll().then(() => {
            if (apply) setNotice(added, 'could not be applied. Please try again.');
          });
        }

        const codeList = readCodes(data);

        let swapped = false;
        surfaces.forEach((s) => {
          const html = data.sections && s.sectionId ? data.sections[s.sectionId] : null;
          if (html && swap(s, html)) swapped = true;
        });

        return Promise.resolve(swapped ? true : resyncAll()).then((settled) => {
          if (!settled) {
            window.location.reload();
            return;
          }

          updateCartCount(data);
          document.dispatchEvent(
            new CustomEvent('cart:change', { detail: { cart: data, source: 'bsb-cart' } })
          );

          if (!apply) {
            announce(origin, 'Discount code removed.');
            focusPromo(origin, null);
            return;
          }

          if (!hasCode(added)) {
            const known = (codeList || []).some(
              (entry) => entry && (entry.code || '').toLowerCase() === added.toLowerCase()
            );

            let text = "isn't a valid discount code.";
            if (known) {
              text = activeCodes().length
                ? "couldn't be used with your existing discounts."
                : "can't be used for this order.";
            }

            announce(origin, `${added} ${text}`);
            setNotice(added, text);
            focusPromo(origin, added);
            return;
          }

          const dropped = before.filter((code) => !hasCode(code));

          if (dropped.length) {
            const text = "couldn't be used with your existing discounts.";
            announce(origin, `Discount code ${added} applied. ${dropped[0]} ${text}`);
            setNotice(dropped[0], text);
          } else {
            announce(origin, `Discount code ${added} applied.`);
          }

          focusPromo(origin, null);
        });
      })
      .catch(() => {
        announce(origin, 'Something went wrong updating your cart.');
        return resyncAll().then(() => {
          if (apply) setNotice(added, 'could not be applied. Please try again.');
        });
      })
      .finally(() => {
        const btn = origin.root.querySelector('[data-bsb-promo-apply]');
        if (btn) btn.disabled = false;
        setBusy(false);
      });
  };

  const request = (origin, key, quantity) => {
    pending.set(key, quantity);
    chain = chain
      .then(() => {
        if (!pending.has(key)) return null;
        const target = pending.get(key);
        pending.delete(key);
        return send(origin, key, target);
      })
      .catch(() => {});
  };

  const currentQty = (item, key) => {
    if (pending.has(key)) return pending.get(key);
    const el = item.querySelector('.bsb-qty__value');
    const value = el ? parseInt(el.textContent.trim(), 10) : NaN;
    return Number.isNaN(value) ? 1 : value;
  };

  const bind = (surface) => {
    surface.root.addEventListener('click', (event) => {
      const groupRemove = event.target.closest('.bsb-crt__group-remove');
      if (groupRemove) {
        const keys = (groupRemove.dataset.groupKeys || '').split(',').filter(Boolean);
        if (keys.length) {
          event.preventDefault();
          if (groupRemove.disabled) return;
          groupRemove.disabled = true;
          chain = chain.then(() => sendBulk(surface, keys)).catch(() => {});
        }
        return;
      }

      const addonAdd = event.target.closest('[data-addon-add]');
      if (addonAdd) {
        event.preventDefault();
        const variantId = addonAdd.dataset.addonAdd;
        if (addonAdd.disabled || !variantId || addonsInFlight.has(variantId)) return;
        addonsInFlight.add(variantId);
        addonAdd.disabled = true;
        addonAdd.setAttribute('aria-busy', 'true');
        const title = addonAdd.dataset.addonTitle || '';
        chain = chain
          .then(() => addItem(surface, variantId, title))
          .catch(() => {})
          .then(() => {
            addonsInFlight.delete(variantId);
            if (addonAdd.isConnected) {
              addonAdd.disabled = false;
              addonAdd.removeAttribute('aria-busy');
            }
          });
        return;
      }

      if (event.target.closest('[data-bsb-promo-dismiss]')) {
        event.preventDefault();
        setNotice(null);
        return;
      }

      const promoRemove = event.target.closest('[data-promo-remove]');
      if (promoRemove) {
        event.preventDefault();
        const code = (promoRemove.dataset.promoRemove || '').trim();
        chain = chain
          .then(() => {
            const remaining = activeCodes().filter(
              (applied) => applied.toLowerCase() !== code.toLowerCase()
            );
            return sendDiscount(surface, remaining, null);
          })
          .catch(() => {});
        return;
      }

      const button = event.target.closest('.bsb-qty__btn, .bsb-crt__item-remove');
      if (!button || button.disabled || !surface.root.contains(button)) return;

      const item = button.closest(ROW);
      const key = item ? item.dataset.key : null;
      if (!item || !key) return;

      event.preventDefault();

      let quantity;
      if (button.classList.contains('bsb-crt__item-remove')) {
        quantity = 0;
      } else {
        const stepper = button.closest('.bsb-qty');
        const min = stepper ? parseInt(stepper.dataset.qtyMin, 10) : NaN;
        const floor = Number.isNaN(min) ? 1 : min;
        const delta = parseInt(button.dataset.qtyDelta, 10) || 0;
        const from = currentQty(item, key);
        quantity = Math.max(floor, from + delta);
        if (quantity === from) return;
        const valueEl = item.querySelector('.bsb-qty__value');
        if (valueEl) valueEl.textContent = quantity;
      }

      request(surface, key, quantity);
    });

    surface.root.addEventListener('submit', (event) => {
      if (event.target.classList.contains('bsb-crt__item-actions')) {
        event.preventDefault();
        return;
      }

      if (!event.target.hasAttribute('data-bsb-promo-form')) return;
      event.preventDefault();

      const submitBtn = event.target.querySelector('[data-bsb-promo-apply]');
      if (submitBtn && submitBtn.disabled) return;

      const input = event.target.querySelector('[data-bsb-promo-input]');
      const code = input ? input.value.trim() : '';
      if (!code) {
        focusPromo(surface, null);
        return;
      }

      if (hasCode(code)) {
        setNotice(code, 'is already applied.');
        return;
      }

      chain = chain
        .then(() => sendDiscount(surface, activeCodes().concat(code), code))
        .catch(() => {});
    });
  };

  const register = (root) => {
    if (!root || root.dataset.bsbCrtInitialized) return null;
    root.dataset.bsbCrtInitialized = 'true';
    const surface = {
      root,
      sectionId: root.dataset.sectionId || '',
      statusEl: root.querySelector('.bsb-crt__status')
    };
    surfaces.push(surface);
    bind(surface);
    watchSticky(surface);
    root.addEventListener('bsb-cart:shown', () => watchSticky(surface));
    return surface;
  };

  const loadCodes = () =>
    fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart) return;
        readCodes(cart);
        paintAll();
      })
      .catch(() => {});

  document.querySelectorAll('.bsb-crt').forEach(register);
  window.bsbCartRegister = register;

  if (surfaces.length) loadCodes();

  document.addEventListener('cart:change', (event) => {
    if (!event.detail || event.detail.source === 'bsb-cart') return;
    readCodes(event.detail.cart);
    updateCartCount(event.detail.cart);
    chain = chain.then(() => resyncAll(true)).catch(() => {});
  });

  document.addEventListener('bsb-cart:added', (event) => {
    if (event.target.classList.contains('bsb-crt--drawer')) return;
    const row = event.detail && event.detail.rows ? event.detail.rows[0] : null;
    if (!row) return;
    requestAnimationFrame(() => {
      const box = row.getBoundingClientRect();
      if (box.top >= 0 && box.bottom <= window.innerHeight) return;
      const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const top = window.scrollY + box.top - (window.innerHeight - box.height) / 2;
      window.scrollTo({ top: Math.max(0, top), behavior: motionOk ? 'smooth' : 'auto' });
    });
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resyncAll();
  });
})();
